import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import {
  getFullName,
  getEmail,
  getTelephone,
} from '@webapp/store/user/selectors';
import { UPDATE_USER_ERROR } from '@webapp/store/user/actions';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import User from '@common/types/user/user';
import { Option } from '@webapp/types/common/option';
import { Country } from '@common/types/data/country';
import { NewInstance } from '@common/types/data/new-instance';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import PlusIcon from '@components/basics/icons/plus';
import BoxHeader from '@components/layout/box-header';
import InputField from '@webapp/components/form/input';
import Typography from '@components/basics/typography';
import SquareAvatar from '@components/basics/square-avatar';
import MotionOpacity from '@components/motion/motion-opacity';
import InputSplitWrapper from '@components/layout/input-split-wrapper';
import AdditionalAccount from '@common/types/data/additional-account';

import DropdownCtrl from '@webapp/controllers/form/dropdown';
import EditUserProfileMenu from '@webapp/controllers/menus/edit-user-profile-menu';
import ButtonFixed from '@components/layout/button-fixed';
import Button from '@components/actions/button';
import UserEmailEditor from './user-email-editor';

interface UserProfileEditorCtrlProps extends Partial<User> {
  className?: string;
  id: string;
  countries: Country[];
  showContinueLater?: boolean;
  onChange: (data: Partial<NewInstance<User>>, isNameUpdate: boolean) => void;
  showWalletAddressBtn?: boolean;
  onSaveChanges: (isNameUpdate: boolean) => void;
}

const mapStateToProps = (state: AppState) => {
  return {
    avatar: state.user.profile?.avatar,
    fullName: getFullName(state),
    address: state.user.profile?.address,
    country: state.user.profile?.country,
    email: getEmail(state),
    telephone: getTelephone(state),
    loggedUserAttributes: state.auth?.user?.attributes,
    errorMsg: state.ui.errors[UPDATE_USER_ERROR] || '',
    id: state.user.profile?.id,
    additionalEmails: state.user.profile?.additionalEmails || [],
    additionalTelephones: state.user.profile?.additionalTelephones || [],
    disableAccountUpdates: Boolean(
      state?.auth?.user?.attributes?.['custom:child_emails']
    ),
    countries: state.categories.countries,
  };
};

export const UserProfileEditorCtrl: React.FC<UserProfileEditorCtrlProps> = (
  props
) => {
  const [filter, setFilter] = React.useState('');
  const [flagPage, setFlagPage] = React.useState(1);
  const [disableSave, setDisableSave] = React.useState(true);

  const [avatar, setAvatar] = React.useState(props.avatar || '');
  const [fullName, setFullName] = React.useState(props.fullName || '');
  const [address, setAddress] = React.useState(props.address?.address || '');
  const [address2, setAddress2] = React.useState(props.address?.address2 || '');
  const [city, setCity] = React.useState(props.address?.city || '');
  const [adState, setAdState] = React.useState(props.address?.state || '');
  const [postalCode, setPostalCode] = React.useState(
    props.address?.postalCode || ''
  );
  const [email, setEmail] = React.useState(props.email || '');
  const [telephone, setTelephone] = React.useState(props.telephone || '');
  const [country, setCountry] = React.useState<Country | undefined>(
    props.address?.country
  );
  const [showAddress2, setShowAddress2] = React.useState(false);

  const { id } = props;

  const countryOptions: Option[] = props.countries.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  React.useEffect(() => {
    setAvatar(props.avatar || '');
    setFullName(props.fullName || '');
    setAddress(props.address?.address || '');
    setAddress2(props.address?.address2 || '');
    setCity(props.address?.city || '');
    setAdState(props.address?.state || '');
    setPostalCode(props.address?.postalCode || '');
    setEmail(props.email || '');
    setTelephone(props.telephone || '');
    setCountry(props.address?.country);
  }, [
    id,
    props.avatar,
    props.fullName,
    props.address,
    props.email,
    props.telephone,
  ]);

  const isFromAddress = (field: string) => {
    return [
      'country',
      'address',
      'address2',
      'city',
      'state',
      'postalCode',
    ].includes(field);
  };

  const handleChange = (field: string, value: string | AdditionalAccount[]) => {
    const newAddress = {
      country,
      address,
      address2,
      city,
      state: adState,
      postalCode,
      isPrimary: true,
    };

    const newUser: Partial<NewInstance<User>> = {
      avatar,
      fullName,
      address: newAddress,
      email,
      telephone,
      country,
    };

    if (isFromAddress(field) && newUser.address) {
      newUser.address[field] = value;
    } else {
      newUser[field] = value;
    }

    if (field === 'country') {
      newUser.country = value;
    }

    return props.onChange(newUser, false);
  };

  const fieldChange = (field: string, setter: any, value: any) => {
    setDisableSave(false);
    setter(value);
    handleChange(field, value);
  };

  const onUserSaveChanges = () => {
    props.onSaveChanges(false);
    setDisableSave(true);
  };

  const getBtnText = () => {
    if (props.showWalletAddressBtn) {
      return 'USER_PROFILE.GENERAL.SAVE_CHANGES_AND_CONNECT_ACCOUNT';
    }
    return 'USER_PROFILE.GENERAL.SAVE_CHANGES';
  };

  return (
    <div className={props.className}>
      <Flex gap={3} flexDirection="column">
        <Box noBorderBottom>
          <AvatarContainer>
            <SquareAvatar src={avatar} username={fullName} />
            <div>
              <Typography
                className="mb-5"
                type="big-title"
                customFont="semi-bold"
              >
                {fullName}
              </Typography>
              <EditUserProfileMenu
                onChange={props.onChange}
                onSaveChanges={props.onSaveChanges}
              />
            </div>
          </AvatarContainer>
        </Box>
        <UserEmailEditor />
        <Box noBorderTop>
          <BoxHeader
            title={<Translate id="USER_PROFILE.GENERAL.ADDRESS.TITLE" />}
          />
          <Flex gap={10} flexDirection="column">
            <InputSplitWrapper inHalf margin="0">
              <DropdownCtrl
                title="Country"
                value={country?.id}
                hasFilter
                filterLabel="Find your country"
                customFilterValue={filter}
                onCustomFilter={setFilter}
                getNextPage={(step) => setFlagPage(flagPage + step)}
                onChange={(value) =>
                  fieldChange(
                    'country',
                    setCountry,
                    props.countries.find((c) => c.id === value)!
                  )
                }
                options={countryOptions}
              />
              <InputField
                value={adState}
                label="State"
                onChange={(value) => fieldChange('state', setAdState, value)}
              />
            </InputSplitWrapper>
            <InputSplitWrapper inHalf margin="0">
              <InputField
                value={city}
                label="City"
                onChange={(value) => fieldChange('city', setCity, value)}
              />
              <InputField
                value={postalCode}
                label="Postal Code"
                onChange={(value) =>
                  fieldChange('postalCode', setPostalCode, value)
                }
              />
            </InputSplitWrapper>
            <InputField
              value={address}
              label="Address Line 1"
              onChange={(value) => fieldChange('address', setAddress, value)}
            />
            <Flex
              className="mt-10"
              gap={10}
              alignItems="center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowAddress2(!showAddress2)}
            >
              <StyledPlusIcon close={showAddress2} />
              <Typography weight="700" type="small">
                <Translate
                  id={
                    showAddress2
                      ? 'USER_PROFILE.GENERAL.HIDE.ADDRESS2'
                      : 'USER_PROFILE.GENERAL.SHOW.ADDRESS2'
                  }
                />
              </Typography>
            </Flex>
            <AnimatePresence exitBeforeEnter>
              {address2 || showAddress2 ? (
                <MotionOpacity>
                  <InputField
                    value={address2}
                    label="Address Line 2"
                    onChange={(value) =>
                      fieldChange('address2', setAddress2, value)
                    }
                  />
                </MotionOpacity>
              ) : null}
            </AnimatePresence>
          </Flex>
        </Box>
        <ButtonFixed fixed>
          <Button
            className={props.showWalletAddressBtn ? 'save-continue-button' : ''}
            customHeight={70}
            disabled={disableSave}
            onClick={() => onUserSaveChanges()}
          >
            <Translate id={getBtnText()} />
          </Button>
        </ButtonFixed>
      </Flex>
    </div>
  );
};

export default connect(mapStateToProps)(styled(UserProfileEditorCtrl)`
  .text {
    margin-bottom: 30px;
  }

  .dropdown {
    position: relative;
    z-index: 9;
  }

  .txtSmall {
    font-size: ${({ theme }) => theme.typography.span};
  }

  .adding-button,
  .resend-button {
    max-height: 48px;
    min-height: 48px;
    width: 100%;
    margin-bottom: 5px;
  }
  .save-continue-button {
    max-width: 200px;
  }

  .menu {
    .open-icon,
    .close-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }
`);

const AvatarContainer = styled(Flex)`
  gap: 30px;
  align-items: center;
`;

const StyledPlusIcon = styled(PlusIcon)`
  width: 16px;
  height: 16px;
  padding: 4px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  path {
    fill: ${({ theme }) => theme.colors.differentBackground};
  }
`;
